/**
 * Subscription / trial access helpers.
 *
 * Backend `checkOrgSubscription` already applies trial hard-expiry vs paid +30 grace.
 * The FE historically re-applied +30 on `subscriptionEndDate` everywhere — that must not
 * apply to trials (or expired trials that still carry a trialEndsAt).
 */

/**
 * @param {object} [org]
 * @returns {boolean}
 */
export const isTrialOrg = (org) => {
  if (!org) return false;
  const { subscriptionStatus, isTrial, trialEndsAt, trialEndDate, subscriptionEndDate } = org;
  if (subscriptionStatus === 'trialing') return true;
  if (isTrial === true) return true;
  const hasTrialWindow = !!(trialEndsAt || trialEndDate);
  // `expired` on its own is ambiguous: paid churn and consultant end clients that
  // never subscribed report it too, so it only counts as a trial with a trial window.
  if (subscriptionStatus === 'expired') return hasTrialWindow;
  // Trial window / residue without a paid subscription end date
  if (hasTrialWindow && !subscriptionEndDate) return true;
  return false;
};

/**
 * White-label partner orgs and their client orgs (`whiteLabelId` is not `'0'`).
 * These are billed outside CYF self-serve Stripe checkout.
 *
 * @param {object} [org]
 * @returns {boolean}
 * @example
 * isWhiteLabelOrganisation({ whiteLabelId: 'abc' }) // true
 */
export const isWhiteLabelOrganisation = (org) => String(org?.whiteLabelId || '0') !== '0';

/**
 * Whether the org may use the product (routes, sidebar, dashboard actions).
 * Prefer backend `hasSubscriptionActive` for trials; only apply +30 grace for paid churn.
 * White-label partners and their clients are not gated on CYF Stripe.
 *
 * @param {object} [org]
 * @returns {boolean}
 */
export const hasUsableSubscription = (org) => {
  if (!org) return false;
  if (org.hasSubscriptionActive === true) return true;
  if (isWhiteLabelOrganisation(org)) return true;

  // Never grant +30 grace to trials / expired trials
  if (isTrialOrg(org)) return false;

  const { subscriptionEndDate } = org;
  if (!subscriptionEndDate) return false;
  const endWithGrace = new Date(subscriptionEndDate);
  endWithGrace.setDate(endWithGrace.getDate() + 30);
  return new Date() <= endWithGrace;
};

/**
 * Resolved feature keys returned by the backend entitlement resolver.
 * Do not use `org.addOns` here; that collection contains factor dataset IDs.
 *
 * @param {object} [org]
 * @returns {string[]}
 */
export const getEntitlementFeatureKeys = (org) =>
  org?.entitlements?.featureKeys || org?.entitlements?.features || [];

/**
 * @param {object} [org]
 * @param {string} featureKey
 * @returns {boolean}
 */
export const hasFeatureEntitlement = (org, featureKey) =>
  getEntitlementFeatureKeys(org).includes(featureKey);

/**
 * @param {object} [org]
 * @returns {Date|null}
 */
export const getTrialEndsAtDate = (org) => {
  const raw = org?.trialEndsAt || org?.trialEndDate;
  if (!raw) return null;
  const d = new Date(raw);
  return Number.isNaN(d.getTime()) ? null : d;
};

/**
 * Whole days remaining until trialEndsAt (0 if ending today, null if unknown).
 * Negative when past end.
 *
 * @param {object} [org]
 * @param {Date} [now]
 * @returns {number|null}
 */
export const getTrialDaysRemaining = (org, now = new Date()) => {
  const end = getTrialEndsAtDate(org);
  if (!end) return null;
  const msPerDay = 24 * 60 * 60 * 1000;
  return Math.ceil((end.getTime() - now.getTime()) / msPerDay);
};

/**
 * @param {object} [org]
 * @returns {boolean}
 */
export const isActiveTrial = (org) => {
  if (!org) return false;
  if (org.subscriptionStatus === 'trialing' || org.isTrial === true) {
    return hasUsableSubscription(org);
  }
  const days = getTrialDaysRemaining(org);
  return days != null && days > 0 && org.hasSubscriptionActive === true;
};

/**
 * @param {object} [org]
 * @returns {boolean}
 */
export const isExpiredTrial = (org) => {
  if (!isTrialOrg(org)) return false;
  if (org.subscriptionStatus === 'expired') return true;
  if (org.hasSubscriptionActive === true) return false;
  const days = getTrialDaysRemaining(org);
  return days != null ? days <= 0 : !org.hasSubscriptionActive;
};

/**
 * Parse free-trial redirect query params from the website lead → Register handoff.
 * Accepts trial=true or trial=1.
 *
 * @param {URLSearchParams|string} [search]
 * @returns {{ isTrial: boolean, leadId: string|null, planId: string|null, fteBand: string|null, fte: number|null, email: string|null, firstName: string|null, lastName: string|null, companyName: string|null, journey: string|null }}
 */
export const parseTrialSignupParams = (search = window.location.search) => {
  const params = typeof search === 'string' ? new URLSearchParams(search) : search;
  const trialRaw = params.get('trial');
  const isTrial = trialRaw === 'true' || trialRaw === '1';
  const fteRaw = params.get('fte');
  const fteNum = fteRaw != null && fteRaw !== '' ? Number(fteRaw) : null;

  return {
    isTrial,
    leadId: params.get('leadId') || null,
    planId: params.get('plan') || null,
    fteBand: params.get('fteBand') || null,
    fte: Number.isFinite(fteNum) ? fteNum : null,
    email: params.get('email') || null,
    firstName: params.get('firstName') || null,
    lastName: params.get('lastName') || null,
    companyName: params.get('companyName') || null,
    journey: params.get('journey') || null,
  };
};

/**
 * Map known registration/trial API error codes to user-facing copy.
 *
 * @param {string} [code]
 * @param {string} [fallback]
 * @returns {string}
 */
export const trialRegistrationErrorMessage = (code, fallback) => {
  const messages = {
    trial_already_used: 'A free trial has already been used for this email or company domain.',
    trial_not_allowed_whitelabel: 'Free trials are only available on Compare Your Footprint direct registration.',
    work_email_required: 'Please use a work email address to start a free trial.',
    rate_limit: 'Too many requests. Please wait a moment and try again.',
    plans_not_available: 'Plans are not available for this account.',
    plan_not_self_serve: 'This plan requires contacting sales.',
    plan_unknown: 'Unknown plan selected.',
  };
  return messages[code] || fallback || 'Registration failed. Please try again.';
};
